import React from "react";
import { PartyPopper, Calendar } from "lucide-react";

/**
 * AnniversaryBanner — surfaces approaching or recently-passed Vault milestones
 * (30 days, 100 days, 1 year, multi-year). The backend computes a window of
 * -7 days (just passed) to +14 days (upcoming). When inside the window, an
 * `anniversary` object lands in the public-profile / insights payload with:
 *   { milestone_days, label, days_until, passed, joined_at }
 *
 * Renders as a confetti-style colored banner across the top of the profile
 * page. Subtle (no shouting animations) but eye-catching.
 */
export default function AnniversaryBanner({ anniversary, accentHex = "#a78bfa" }) {
  if (!anniversary) return null;
  const { label, days_until, passed } = anniversary;

  const headline = passed
    ? label
    : days_until === 0
      ? `TODAY · ${label}`
      : `${days_until} DAY${days_until === 1 ? "" : "S"} UNTIL ${label}`;

  return (
    <div
      data-testid="anniversary-banner"
      className="relative overflow-hidden border mb-6"
      style={{ borderColor: `${accentHex}55`, backgroundColor: `${accentHex}10` }}
    >
      {/* Confetti dots — purely decorative */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        {Array.from({ length: 24 }).map((_, i) => (
          <span
            key={i}
            className="absolute w-1 h-1 rounded-full"
            style={{
              backgroundColor: accentHex,
              left: `${(i * 37) % 100}%`,
              top: `${(i * 53) % 100}%`,
              transform: `rotate(${i * 23}deg)`,
            }}
          />
        ))}
      </div>
      <div className="relative px-4 sm:px-6 py-3 flex items-center gap-3">
        <PartyPopper size={18} style={{ color: accentHex }} className="shrink-0" />
        <div className="flex-1 min-w-0">
          <div
            className="font-heading font-black uppercase tracking-tight text-sm sm:text-base leading-none"
            style={{ color: accentHex }}
          >
            {headline}
          </div>
          <div className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 mt-1 flex items-center gap-1.5">
            <Calendar size={9} />
            {passed ? "MILESTONE REACHED" : "MILESTONE INCOMING"}
          </div>
        </div>
      </div>
    </div>
  );
}
